import type { ComponentProps, ReactNode } from "react";
import { Form } from "@inertiajs/react";
import { cn } from "@/lib/utils";

export function FormContent({
  align = "start",
  className,
  ...props
}: ComponentProps<"div"> & { align?: "start" | "center" }) {
  return (
    <div
      data-form-content=""
      className={cn(
        "@container/form-content w-full min-w-0 max-w-[45rem] space-y-6",
        align === "center" && "mx-auto",
        className,
      )}
      {...props}
    />
  );
}

export function FormSection({
  title,
  description,
  children,
  className,
  ...props
}: Omit<ComponentProps<"section">, "title"> & {
  title?: ReactNode;
  description?: ReactNode;
}) {
  return (
    <section className={cn("min-w-0 space-y-4", className)} {...props}>
      {(title || description) && (
        <header className="max-w-prose space-y-2">
          {title && <h2 className="text-section font-semibold">{title}</h2>}
          {description && (
            <div className="text-sm text-muted-foreground">{description}</div>
          )}
        </header>
      )}
      {children}
    </section>
  );
}

export function FormFields({
  paired = true,
  className,
  ...props
}: ComponentProps<"div"> & { paired?: boolean }) {
  return (
    <div
      className={cn("form-fields", className)}
      data-paired={paired}
      {...props}
    />
  );
}

export function FilterToolbar({
  columns = "search-state",
  className,
  ...props
}: ComponentProps<typeof Form> & {
  columns?: "search-state" | "search" | "auto";
}) {
  return (
    <div className="@container/filter-toolbar w-full min-w-0">
      <Form
        className={cn("filter-toolbar", className)}
        data-filter-columns={columns}
        {...props}
      />
    </div>
  );
}

export function PageCanvas({
  className,
  as: Tag = "div",
  padded = true,
  ...props
}: ComponentProps<"div"> & { as?: "div" | "main"; padded?: boolean }) {
  return (
    <Tag
      {...(Tag === "main" ? { id: "main-content", tabIndex: -1 } : {})}
      data-page-canvas=""
      data-layout-container=""
      className={cn(
        "w-full min-w-0 space-y-section",
        padded && "p-page",
        className,
      )}
      {...props}
    />
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0 space-y-2">
        <h1 className="text-page font-display break-words">{title}</h1>
        {description && (
          <div className="text-muted-foreground text-sm">{description}</div>
        )}
      </div>
      {actions && <ActionBar>{actions}</ActionBar>}
    </header>
  );
}

export function ActionBar({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn("flex flex-wrap items-center gap-3", className)}
      {...props}
    />
  );
}

export function RecordSummary({
  title,
  description,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="min-w-0 space-y-3 rounded-lg border bg-card p-4">
      <h2 className="font-semibold break-words">{title}</h2>
      {description && (
        <p className="text-muted-foreground text-sm break-words">
          {description}
        </p>
      )}
      {children}
    </section>
  );
}
